import React, { useState, useEffect } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Jira } from '../../services/Contexture/Jira';
import {
  setIntegrationAvailable,
  setIntegrationData,
} from '../../redux/slices/integrationsSlice';
import { LoadingButton } from '../LoadingButton';
import Divider from '../Divider/Divider';
import {
  StyledModal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  FormSection,
  SectionTitle,
  SectionDescription,
  CancelButton,
  SaveButton,
} from './IntegrationModal.styles';

const ATLASSIAN_TOKEN_URL =
  'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/';

const JiraConfigModal = ({ isOpen, onClose, onSave, isConnected, currentConfig }) => {
  const dispatch = useDispatch();

  const [url, setUrl] = useState('');
  const [username, setUsername] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setUrl(currentConfig?.url || '');
      setUsername(currentConfig?.username || '');
      setApiToken('');
      setConnectionResult(null);
      setErrors({});
    }
  }, [isOpen, currentConfig]);

  const validate = () => {
    const errs = {};
    if (!url.trim()) {
      errs.url = 'JIRA hostname is required';
    } else if (!url.trim().startsWith('https://')) {
      errs.url = 'URL must use HTTPS';
    }
    if (!username.trim()) {
      errs.username = 'Username is required';
    }
    if (!isConnected && !apiToken.trim()) {
      errs.apiToken = 'API token is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setConnectionResult(null);

    try {
      let result;
      if (isConnected) {
        const updates = {};
        if (url.trim()) updates.url = url.trim();
        if (username.trim()) updates.username = username.trim();
        if (apiToken.trim()) updates.apiToken = apiToken.trim();
        result = await Jira.updateConfig(updates);
      } else {
        result = await Jira.setup({
          url: url.trim(),
          username: username.trim(),
          apiToken: apiToken.trim(),
        });
      }

      const testResult = result?.connectionTest || result;
      setConnectionResult(testResult);

      if (testResult?.status === 'connected') {
        toast.success(
          isConnected
            ? 'JIRA configuration updated successfully'
            : 'JIRA integration connected successfully',
          { position: 'top-right', autoClose: 4000 }
        );

        dispatch(setIntegrationAvailable({ id: 'jira', available: true }));

        const config = result?.config || await Jira.getConfig().catch(() => null);
        dispatch(setIntegrationData({ id: 'jira', data: { meta: { config } } }));

        onSave?.();
      } else {
        toast.error(
          testResult?.error || 'Connection test failed. Please verify your credentials.',
          { position: 'top-right', autoClose: 4000 }
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to configure JIRA';
      toast.error(message, { position: 'top-right', autoClose: 4000 });
      setConnectionResult({ status: 'disconnected', error: message });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StyledModal show={isOpen} onHide={onClose} centered size='lg'>
      <ModalHeader closeButton>
        <ModalTitle>
          {isConnected ? 'Update JIRA Configuration' : 'Connect JIRA'}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <SectionTitle>JIRA credentials</SectionTitle>
        <SectionDescription>
          You need a dedicated service account user in your JIRA instance with an
          API token. The user must have access to the projects you want to
          monitor.
        </SectionDescription>

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <Form.Group className='mb-3'>
              <Form.Label>JIRA Instance URL</Form.Label>
              <Form.Control
                type='url'
                placeholder='https://yourcompany.atlassian.net'
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                isInvalid={!!errors.url}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.url}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Username</Form.Label>
              <Form.Control
                type='text'
                placeholder='securitybulldog@yourcompany.com'
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.username}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>API Token</Form.Label>
              <Form.Control
                type='password'
                placeholder={
                  isConnected ? '(leave blank to keep current)' : 'Enter API token'
                }
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                isInvalid={!!errors.apiToken}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.apiToken}
              </Form.Control.Feedback>
              <Form.Text>
                Generate at Atlassian: Personal Settings &rarr; Security &rarr;{' '}
                <a href={ATLASSIAN_TOKEN_URL} target='_blank' rel='noreferrer'>
                  Create and manage API tokens
                </a>
              </Form.Text>
            </Form.Group>
          </FormSection>

          {connectionResult && (
            <>
              <Divider label='Connection test' />
              <Alert
                variant={
                  connectionResult.status === 'connected' ? 'success' : 'danger'
                }
              >
                {connectionResult.status === 'connected'
                  ? `Connected successfully${
                      connectionResult.projectCount
                        ? ` (${connectionResult.projectCount} projects found)`
                        : ''
                    }`
                  : `Connection failed: ${connectionResult.error || 'Unknown error'}`}
              </Alert>
            </>
          )}
        </Form>
      </ModalBody>

      <ModalFooter>
        <CancelButton onClick={onClose} disabled={isSaving}>
          Cancel
        </CancelButton>
        <LoadingButton
          as={SaveButton}
          onClick={handleSubmit}
          loading={isSaving}
          loadingText='Testing connection...'
        >
          {isConnected ? 'Update Configuration' : 'Connect to JIRA'}
        </LoadingButton>
      </ModalFooter>
    </StyledModal>
  );
};

export default JiraConfigModal;
